import React, { useEffect, useMemo, useState } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Alert,
  Box,
  Button,
  Card,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import axios from "../../services/api/axios";
import BrandSpinner from "../../shared/components/BrandSpinner";
import BrandPagination from "../../layouts/Pagination";
import SearchBar from "../../shared/components/SearchBar";
import EntityProfile from "../../shared/components/EntityProfile";
import { contactProfileConfig } from "../../shared/profile-configs/ContactProfileConfig";
import { retailProfileConfig } from "../../shared/profile-configs/RetailProfileConfig";
import { normalizeRetailConceptRecord } from "../../utils/retailCategoryNormalization";
import RetailTable from "./RetailTable";
import {
  ACTIONS_HEADER_BTN_SX,
  ACTION_MENU_PAPER_SX,
} from "../../theme/buttonstyles";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};

export default function Retail() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [subsectorFilter, setSubsectorFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState(null);
  const [orderDirection, setOrderDirection] = useState("desc");
  const pageSize = 50;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactProfileOpen, setContactProfileOpen] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  useEffect(() => {
    let active = true;
    const fetchRetailConcepts = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get("/retail-concepts");
        const rows = Array.isArray(response.data)
          ? response.data.map((row) => normalizeRetailConceptRecord(row))
          : [];
        if (!active) return;
        setData(rows);
        setFilteredData(rows);
      } catch (err) {
        if (!active) return;
        console.error(err);
        setError("Failed to load retail concepts.");
        setData([]);
        setFilteredData([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchRetailConcepts();
    return () => {
      active = false;
    };
  }, []);

  const sectorOptions = useMemo(
    () =>
      [
        ...new Set(
          data
            .map((item) => String(item?.retail_sector || "").trim())
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [data],
  );
  const subsectorOptions = useMemo(
    () =>
      [
        ...new Set(
          data
            .map((item) => String(item?.retail_subsector || "").trim())
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [data],
  );
  const countryOptions = useMemo(
    () =>
      [
        ...new Set(
          data
            .map((item) => String(item?.country || "").trim())
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [data],
  );

  useEffect(() => {
    const normalizedSearch = search.trim().toLowerCase();
    setFilteredData(
      data.filter((item) => {
        const matchesSearch =
          !normalizedSearch ||
          [
            item?.concept,
            item?.country,
            item?.retail_sector,
            item?.retail_subsector,
          ].some((v) =>
            String(v || "")
              .toLowerCase()
              .includes(normalizedSearch),
          );
        const matchesSector =
          !sectorFilter ||
          String(item?.retail_sector || "").trim() === sectorFilter;
        const matchesSubsector =
          !subsectorFilter ||
          String(item?.retail_subsector || "").trim() === subsectorFilter;
        const matchesCountry =
          !countryFilter ||
          String(item?.country || "").trim() === countryFilter;
        return (
          matchesSearch && matchesSector && matchesSubsector && matchesCountry
        );
      }),
    );
  }, [countryFilter, data, search, sectorFilter, subsectorFilter]);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    sectorFilter,
    subsectorFilter,
    countryFilter,
    orderBy,
    orderDirection,
  ]);

  const sortedFilteredData = useMemo(() => {
    if (orderBy !== "openings_closings_next_12_months") return filteredData;
    return [...filteredData].sort((a, b) => {
      const aValue = Number(a?.openings_closings_next_12_months ?? 0);
      const bValue = Number(b?.openings_closings_next_12_months ?? 0);
      if (aValue === bValue)
        return String(a?.concept || "").localeCompare(String(b?.concept || ""));
      return orderDirection === "asc" ? aValue - bValue : bValue - aValue;
    });
  }, [filteredData, orderBy, orderDirection]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(sortedFilteredData.length / pageSize)),
    [sortedFilteredData.length],
  );
  const safePage = Math.min(page, totalPages);
  const paginatedRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedFilteredData.slice(start, start + pageSize);
  }, [sortedFilteredData, safePage]);

  const handleSortRequest = (key) => {
    const isAsc = orderBy === key && orderDirection === "asc";
    setOrderBy(key);
    setOrderDirection(isAsc ? "desc" : "asc");
  };
  const handlePageChange = (_e, nextPage) => setPage(nextPage);

  const handleOpenProfile = (company) => {
    setSelectedCompany(company);
    setProfileOpen(true);
  };
  const handleCloseProfile = () => {
    setProfileOpen(false);
    setSelectedCompany(null);
  };

  const handleOpenContactProfile = (company) => {
    const contactName =
      company?.contact_full_name ||
      [company?.contact_first_name, company?.contact_last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
    setSelectedContact({
      contact_name: contactName || "Retail Contact",
      title: company?.contact_title || null,
      email: company?.contact_email || null,
      phone: company?.contact_phone || null,
      city: company?.contact_city || null,
      state: company?.contact_state || null,
      country: company?.country || null,
      company_name: company?.concept || null,
      linkedin_url: company?.linkedin_url || null,
    });
    setContactProfileOpen(true);
  };
  const handleCloseContactProfile = () => {
    setContactProfileOpen(false);
    setSelectedContact(null);
  };

  const handleExport = async () => {
    const exportRows = sortedFilteredData.map((row) => ({
      "Company Name": row?.concept || "",
      "Openings/Closings": row?.openings_closings_next_12_months ?? "",
      "Unit Count": row?.unit_count_end_2025 ?? "",
      "Typical Store Size Sq Ft": row?.typical_store_size_median ?? "",
      Sector: row?.retail_sector || "",
      Subsector: row?.retail_subsector || "",
      Country: row?.country || "",
    }));
    try {
      const XLSX = await import("xlsx");
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Retail");
      XLSX.writeFile(workbook, "retail_export.xlsx");
    } catch (err) {
      console.error(err);
    }
  };

  const profileSections = useMemo(
    () => (selectedCompany ? retailProfileConfig(selectedCompany) : []),
    [selectedCompany],
  );
  const contactSections = useMemo(
    () => (selectedContact ? contactProfileConfig(selectedContact) : []),
    [selectedContact],
  );

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        pb: 4,
      }}
    >
      {/* ===== Sticky Header ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          {/* Left: icon + title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: BRAND_GREEN,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <StorefrontIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: BRAND_BLUE,
                  fontSize: 21,
                  fontWeight: 800,
                  lineHeight: 1.05,
                }}
              >
                Retail
              </Typography>
              <Typography sx={{ color: "#3a5c08", fontFamily: "'DM Sans', sans-serif", fontWeight: 500, letterSpacing: 0.3, fontSize: 12.5, mt: 0.35 }}>
                Retail concepts &amp; expansion trends
              </Typography>
            </Box>
          </Box>

          {/* Center: search */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search Company, Country, Sector, or Subsector…"
              maxWidth="100%"
              containerSx={{ justifyContent: "center", width: "100%" }}
              wrapperSx={{ maxWidth: "100%" }}
            />
          </Box>

          {/* Right: actions */}
          <Box sx={{ flexShrink: 0 }}>
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setActionsAnchorEl(e.currentTarget)}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>
            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={() => setActionsAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem
                onClick={() => {
                  setActionsAnchorEl(null);
                  navigate("/retail/trends");
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: BRAND_GREEN }}>
                  <TrendingUpIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Retail Trends</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setActionsAnchorEl(null);
                  handleExport();
                }}
                disabled={sortedFilteredData.length === 0}
              >
                <ListItemIcon sx={{ minWidth: 32, color: BRAND_BLUE }}>
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* ===== Content Area ===== */}
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <BrandSpinner compact label="Loading retail concepts…" py={8} />
        ) : paginatedRows.length > 0 ? (
          <>
            <Card sx={CARD_SX}>
              <RetailTable
                rows={paginatedRows}
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={handleSortRequest}
                sectorFilter={sectorFilter}
                onSectorFilterChange={setSectorFilter}
                sectorOptions={sectorOptions}
                subsectorFilter={subsectorFilter}
                onSubsectorFilterChange={setSubsectorFilter}
                subsectorOptions={subsectorOptions}
                countryFilter={countryFilter}
                onCountryFilterChange={setCountryFilter}
                countryOptions={countryOptions}
                onOpenProfile={handleOpenProfile}
                onOpenContactProfile={handleOpenContactProfile}
              />
            </Card>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <BrandPagination
                page={safePage}
                count={totalPages}
                onChange={handlePageChange}
              />
            </Box>
          </>
        ) : (
          <Card sx={CARD_SX}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  border: "1px dashed #dce4ef",
                  borderRadius: 2,
                  px: 6,
                  py: 5,
                }}
              >
                <StorefrontIcon
                  sx={{ fontSize: 40, color: "#b0bdd0", mb: 1.5 }}
                />
                <Typography
                  sx={{
                    color: "#344863",
                    fontWeight: 700,
                    fontSize: 15,
                    mb: 0.5,
                  }}
                >
                  No retail concepts found
                </Typography>
                <Typography
                  sx={{ color: "#8a98b3", fontSize: 13, fontStyle: "italic" }}
                >
                  Adjust the search or filters and try again.
                </Typography>
              </Box>
            </Box>
          </Card>
        )}
      </Box>

      <EntityProfile
        open={profileOpen}
        onClose={handleCloseProfile}
        entity={{ ...selectedCompany, name: selectedCompany?.concept }}
        sections={profileSections}
      />
      <EntityProfile
        open={contactProfileOpen}
        onClose={handleCloseContactProfile}
        entity={{
          ...selectedContact,
          title: selectedContact?.contact_name,
          company_name: selectedContact?.company_name,
        }}
        sections={contactSections}
      />
    </Box>
  );
}
